import type { FitnessDimensionType } from "@components/fitness/types";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/screens/plan/constants/planCheckupDimensions";
import { GoalCategoryEnum } from "@features/api/TrackerApi";
import type { PlanCheckupDomainId } from "./planCheckupTypes";
import { getPlanCheckupDomainLabel } from "./planCheckupRingTokens";

const I18n = I18nUtil.createScoped(Translations);

type PlanCheckupHookId =
  "idle_cash" | "tax_room" | "concentration" | "affordability" | "runway";

export type PlanCheckupDimensionMeta = {
  sort: number;
  docId: string;
  dimension: number;
  /** 子维度所属的财务体质维度（Control/Safety/Growth/Peace/Fluency） */
  dimensionType: FitnessDimensionType;
  category: GoalCategoryEnum;
  domainId: PlanCheckupDomainId;
  nameCn: string;
  nameEn: string;
  hookCopyKey: string;
  hookId?: PlanCheckupHookId;
  isTop5: boolean;
};

const CATEGORY_DOMAIN: Record<GoalCategoryEnum, PlanCheckupDomainId> = {
  [GoalCategoryEnum.FINANCIAL_SECURITY]: "financial_security",
  [GoalCategoryEnum.CASH_OPTIMIZATION]: "cash_optimization",
  [GoalCategoryEnum.WEALTH_BUILDING]: "wealth_building",
  [GoalCategoryEnum.LIFE_DECISIONS]: "life_decisions",
};

function row(
  partial: Omit<PlanCheckupDimensionMeta, "domainId" | "isTop5"> & {
    isTop5?: boolean;
  },
): PlanCheckupDimensionMeta {
  return {
    ...partial,
    domainId: CATEGORY_DOMAIN[partial.category],
    isTop5: partial.isTop5 ?? false,
  };
}

/** SPEC §3 — 16 checkup dimensions (sort asc = list order) */
export const PLAN_CHECKUP_DIMENSIONS: readonly PlanCheckupDimensionMeta[] = [
  row({
    sort: 1,
    docId: "Cash-3",
    dimension: 103,
    dimensionType: "growth",
    category: GoalCategoryEnum.CASH_OPTIMIZATION,
    nameCn: "闲钱偷懒",
    nameEn: "Idle cash",
    hookCopyKey: "Is your cash just sitting there?",
    hookId: "idle_cash",
    isTop5: true,
  }),
  row({
    sort: 2,
    docId: "Tax-1",
    dimension: 201,
    dimensionType: "fluency",
    category: GoalCategoryEnum.WEALTH_BUILDING,
    nameCn: "免税空间没用满",
    nameEn: "Tax-advantaged space",
    hookCopyKey: "How much are you overpaying the IRS?",
    hookId: "tax_room",
    isTop5: true,
  }),
  row({
    sort: 3,
    docId: "Invest-1",
    dimension: 301,
    dimensionType: "safety",
    category: GoalCategoryEnum.WEALTH_BUILDING,
    nameCn: "持仓过度集中",
    nameEn: "Portfolio concentration",
    hookCopyKey: "Is one stock running your whole future?",
    hookId: "concentration",
    isTop5: true,
  }),
  row({
    sort: 4,
    docId: "Housing-1",
    dimension: 401,
    dimensionType: "peace",
    category: GoalCategoryEnum.LIFE_DECISIONS,
    nameCn: "能不能买得起",
    nameEn: "Home affordability",
    hookCopyKey: "Can you actually afford a home?",
    hookId: "affordability",
    isTop5: true,
  }),
  row({
    sort: 5,
    docId: "Cash-1",
    dimension: 101,
    dimensionType: "control",
    category: GoalCategoryEnum.FINANCIAL_SECURITY,
    nameCn: "失业撑多久",
    nameEn: "Income runway",
    hookCopyKey: "How long could you last with no income?",
    hookId: "runway",
    isTop5: true,
  }),
  row({
    sort: 6,
    docId: "Invest-3",
    dimension: 303,
    dimensionType: "safety",
    category: GoalCategoryEnum.WEALTH_BUILDING,
    nameCn: "雇主敞口",
    nameEn: "Employer concentration",
    hookCopyKey: "Is your employer also your biggest investment?",
  }),
  row({
    sort: 7,
    docId: "Cash-2",
    dimension: 102,
    dimensionType: "safety",
    category: GoalCategoryEnum.FINANCIAL_SECURITY,
    nameCn: "应急金缺口",
    nameEn: "Emergency fund gap",
    hookCopyKey: "Is your safety net actually a net?",
  }),
  row({
    sort: 8,
    docId: "Housing-2",
    dimension: 402,
    dimensionType: "peace",
    category: GoalCategoryEnum.LIFE_DECISIONS,
    nameCn: "首付准备度",
    nameEn: "Down payment readiness",
    hookCopyKey: "How close are you to your down payment?",
  }),
  row({
    sort: 9,
    docId: "Invest-2",
    dimension: 302,
    dimensionType: "growth",
    category: GoalCategoryEnum.WEALTH_BUILDING,
    nameCn: "配置不匹配",
    nameEn: "Risk alignment",
    hookCopyKey: "Does your portfolio match your risk appetite?",
  }),
  row({
    sort: 10,
    docId: "Tax-2",
    dimension: 202,
    dimensionType: "fluency",
    category: GoalCategoryEnum.WEALTH_BUILDING,
    nameCn: "RSU 预扣不足",
    nameEn: "RSU tax withholding",
    hookCopyKey: "Will your RSUs leave a surprise tax bill?",
  }),
  row({
    sort: 11,
    docId: "Cash-5",
    dimension: 105,
    dimensionType: "control",
    category: GoalCategoryEnum.CASH_OPTIMIZATION,
    nameCn: "存钱率",
    nameEn: "Savings rate",
    hookCopyKey: "Earning a lot but keeping none of it?",
  }),
  row({
    sort: 12,
    docId: "Cash-4",
    dimension: 104,
    dimensionType: "control",
    category: GoalCategoryEnum.CASH_OPTIMIZATION,
    nameCn: "低息债 vs 投资",
    nameEn: "Debt vs invest",
    hookCopyKey: "Pay down debt, or invest it?",
  }),
  row({
    sort: 13,
    docId: "Cash-6",
    dimension: 106,
    dimensionType: "control",
    category: GoalCategoryEnum.CASH_OPTIMIZATION,
    nameCn: "支出漂移",
    nameEn: "Spending drift",
    hookCopyKey: "Where is your money actually leaking?",
  }),
  row({
    sort: 14,
    docId: "Housing-3",
    dimension: 403,
    dimensionType: "peace",
    category: GoalCategoryEnum.LIFE_DECISIONS,
    nameCn: "该租还是买",
    nameEn: "Rent vs buy",
    hookCopyKey: "Rent or buy — which is smarter for you?",
  }),
  row({
    sort: 15,
    docId: "Cash-7",
    dimension: 107,
    dimensionType: "safety",
    category: GoalCategoryEnum.FINANCIAL_SECURITY,
    nameCn: "自雇稳定性",
    nameEn: "Self-employment stability",
    hookCopyKey: "One bad month away from trouble?",
  }),
  row({
    sort: 16,
    docId: "Tax-3",
    dimension: 203,
    dimensionType: "fluency",
    category: GoalCategoryEnum.LIFE_DECISIONS,
    nameCn: "报税优化动作",
    nameEn: "Year-end tax moves",
    hookCopyKey: "Are you missing year-end tax moves?",
  }),
] as const;

const BY_DIMENSION = new Map(
  PLAN_CHECKUP_DIMENSIONS.map((d) => [d.dimension, d]),
);

export function getCheckupDimensionMeta(
  dimension: number,
): PlanCheckupDimensionMeta | undefined {
  return BY_DIMENSION.get(dimension);
}

/** plan_guide URL `code` / Chat question — Goal.dimension int as string (101–403) */
export function toPlanGuideDimensionCode(
  dimension: number,
): string | undefined {
  const meta = getCheckupDimensionMeta(dimension);
  if (!meta) {
    return undefined;
  }
  return String(meta.dimension);
}

export function getCheckupDomainLabel(
  domainId: PlanCheckupDomainId,
  locale?: string,
): string {
  return getPlanCheckupDomainLabel(domainId, locale);
}

export function getCheckupHookCopy(
  meta: PlanCheckupDimensionMeta,
  locale?: string,
): string {
  return I18n.t(meta.hookCopyKey, locale ? { locale } : undefined);
}

/** FF §3.4.3 — 16 体检项 → FF 五维映射 */
export const DIMENSION_FITNESS_MAP: Record<FitnessDimensionType, number[]> =
  PLAN_CHECKUP_DIMENSIONS.reduce<Record<FitnessDimensionType, number[]>>(
    (acc, meta) => {
      acc[meta.dimensionType].push(meta.dimension);
      return acc;
    },
    {
      control: [],
      safety: [],
      growth: [],
      fluency: [],
      peace: [],
    },
  );

export type DimensionFilterCode = "all" | FitnessDimensionType;
export const DIMENSION_FILTER_OPTIONS: DimensionFilterCode[] = [
  "all",
  "control",
  "safety",
  "growth",
  "peace",
  "fluency",
];

/** Figma `92100:51887` — Completed checkups 区块卡片自上而下域顺序 */
const PLAN_CHECKUP_COMPLETED_DOMAIN_ORDER: readonly PlanCheckupDomainId[] = [
  "cash_optimization",
  "financial_security",
  "wealth_building",
  "life_decisions",
];

const DIMENSION_SORT_BY_ID = new Map(
  PLAN_CHECKUP_DIMENSIONS.map((d) => [d.dimension, d.sort]),
);

/** Completed 卡展示序：先 Figma 域序，同域内按 dimension `sort` 升序 */
export function compareCheckupCompletedCards(
  a: { domainId: PlanCheckupDomainId; dimension: number },
  b: { domainId: PlanCheckupDomainId; dimension: number },
): number {
  const domainRank = (id: PlanCheckupDomainId) =>
    PLAN_CHECKUP_COMPLETED_DOMAIN_ORDER.indexOf(id);
  const domainDelta = domainRank(a.domainId) - domainRank(b.domainId);
  if (domainDelta !== 0) {
    return domainDelta;
  }
  return (
    (DIMENSION_SORT_BY_ID.get(a.dimension) ?? 0) -
    (DIMENSION_SORT_BY_ID.get(b.dimension) ?? 0)
  );
}
